import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import * as Sentry from '@sentry/react'
import './index.css'
import App from './App.tsx'
import ErrorBoundary from './components/ErrorBoundary'
import { AuthProvider } from './auth/AuthContext'
import { syncFromSupabase } from './hooks/usePersist'
import { setupSyncEngine } from './store/syncEngine'
import { reportWebVitals } from './services/webVitals'
import { getEnvConfig } from './config/env'

// � Validate environment variables early
const envConfig = getEnvConfig();

// �🔍 Initialize Sentry for error tracking
if (import.meta.env.PROD && envConfig.sentryDsn) {
  Sentry.init({
    dsn: envConfig.sentryDsn,
    environment: import.meta.env.MODE,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],
    // Performance Monitoring
    tracesSampleRate: 0.1, // 10% of transactions for performance monitoring
    // Session Replay
    replaysSessionSampleRate: 0.1, // 10% of sessions
    replaysOnErrorSampleRate: 1.0, // 100% of sessions with errors
    // Filter out known noise
    ignoreErrors: [
      'ResizeObserver loop limit exceeded',
      'Non-Error promise rejection captured',
    ],
    beforeSend(event) {
      // Don't send errors from localhost
      if (window.location.hostname === 'localhost') return null;
      // Add custom context
      if (event.user) {
        event.user.segment = 'mushroom-grower';
      }
      return event;
    },
  });
}

// Async hataları ve yakalanmayan promise rejection'ları Sentry'ye ilet.
// React Error Boundary sadece render-phase hatalarını yakalar; bu global handler
// event handler'ları, setTimeout ve API çağrılarındaki hataları da kapsar.
window.addEventListener('unhandledrejection', (event) => {
  console.error('[GMC] Yakalanmayan promise hatası:', event.reason);
  if (import.meta.env.PROD) {
    Sentry.captureException(event.reason ?? new Error('Unhandled rejection'), {
      tags: { type: 'unhandledrejection' },
    });
  }
});

window.addEventListener('error', (event) => {
  if (import.meta.env.PROD) {
    Sentry.captureException(event.error ?? new Error(event.message), {
      tags: { type: 'global-error' },
    });
  }
});

// Recharts 3 fires a transient warning on initial render before ResizeObserver
// measures the container (width=-1, height=-1). All containers have explicit
// pixel heights; the warning resolves on the next tick and is not actionable.
const _warn = console.warn.bind(console);
console.warn = (...args: unknown[]) => {
  if (typeof args[0] === 'string' && args[0].startsWith('The width(') && args[0].includes('height(')) return;
  _warn(...args);
};

// 🍄 Easter egg — meraklı geliştirici için
if (typeof console !== 'undefined') {
  console.log(
    '%c🍄 Gurme Mantar Komuta',
    'font-size:16px; font-weight:600; color:#6ee7b7; background:#0f1115; padding:6px 12px; border-radius:6px;'
  );
  console.log(
    '%cSensörler nominal. Misel ağı büyüyor.',
    'font-size:11px; color:#6ee7b780; font-family:monospace;'
  );
}

setupSyncEngine()
syncFromSupabase()
  .catch((err) => {
    console.warn('[GMC] Supabase sync atlandı — localStorage ile devam ediliyor:', err)
    // Report sync errors to Sentry
    if (import.meta.env.PROD) {
      Sentry.captureException(err, {
        tags: { component: 'sync-engine' },
        level: 'warning',
      });
    }
  })
  .then(() => {
    createRoot(document.getElementById('root')!).render(
      <StrictMode>
        <ErrorBoundary>
          <AuthProvider>
            <BrowserRouter>
              <App />
            </BrowserRouter>
          </AuthProvider>
        </ErrorBoundary>
      </StrictMode>,
    )
    
    // 📊 Start Web Vitals monitoring with Sentry integration
    reportWebVitals((metric) => {
      // Send Web Vitals to Sentry
      if (import.meta.env.PROD) {
        const { name, value, rating } = metric;
        Sentry.setMeasurement(name, value, 'millisecond');
        
        // Send poor/needs-improvement scores as events
        if (rating !== 'good') {
          Sentry.captureMessage(`Poor Web Vital: ${name}`, {
            level: 'warning',
            tags: {
              metric: name,
              rating,
            },
            extra: { value },
          });
        }
      }
    });
  })
